// Client-side export of product content (title/description/reviews) as a
// plain-text block or CSV — feeds review generation and other LLM workflows.

export function downloadFile(filename, content, mime = "text/plain") {
  const url = URL.createObjectURL(
    new Blob([content], { type: `${mime};charset=utf-8` }),
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export function slugify(s) {
  return (
    String(s || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 60) || "product"
  );
}

export function productExportText(p) {
  return [
    `Title: ${p.name || ""}`,
    `Description: ${p.description || ""}`,
    `Reviews: ${p.reviews || ""}`,
  ].join("\n\n");
}

// The trailing three columns ship empty: they're the ones the review
// generator (or a human) fills in before the CSV comes back through
// "Import Reviews". Existing my_review/my_rating are pre-filled so a
// re-export doesn't look blank for already-reviewed items.
export function productsToCsv(rows) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  // BOM so Excel reads review text (curly quotes, accents) as UTF-8.
  return "\ufeff" + [
    "Id,Title,Description,Reviews,Review Title,Review Content,Rating",
    ...rows.map((p) => {
      const myReview = String(p.my_review ?? "");
      const nl = myReview.indexOf("\n");
      const reviewTitle = nl === -1 ? "" : myReview.slice(0, nl).trim();
      const reviewContent = nl === -1 ? myReview : myReview.slice(nl + 1).trim();
      return [
        esc(p.id),
        esc(p.name),
        esc(p.description),
        esc(p.reviews),
        esc(reviewTitle),
        esc(reviewContent),
        esc(p.my_rating ?? ""),
      ].join(",");
    }),
  ].join("\r\n");
}

// Minimal RFC-4180 parser: handles quoted fields, doubled quotes, and
// newlines inside quotes (which review text is full of). Returns rows of
// string cells, including the header row.
export function parseCsv(text) {
  // Excel's "CSV UTF-8" prepends a BOM, which would otherwise glue itself to
  // the first header and make the "Id" column unrecognisable.
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);
  const rows = [];
  let row = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      row.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(field);
      field = "";
      if (row.length > 1 || row[0] !== "") rows.push(row);
      row = [];
    } else {
      field += ch;
    }
  }
  if (field !== "" || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

// Parse a review-import CSV (the export format plus "Review Title",
// "Review Content", and "Rating" columns) into per-entity updates:
// {id, my_review, my_rating}. Rows without an id or any review data are
// dropped. Throws with a user-readable message on a bad header.
export function parseReviewImportCsv(text) {
  const rows = parseCsv(text);
  if (rows.length < 2) throw new Error("CSV has no data rows");
  const header = rows[0].map((h) => h.trim().toLowerCase());
  const idIdx = header.indexOf("id");
  const titleIdx = header.indexOf("review title");
  const contentIdx = header.indexOf("review content");
  const ratingIdx = header.indexOf("rating");
  if (idIdx === -1 || (titleIdx === -1 && contentIdx === -1)) {
    throw new Error(
      'CSV needs an "Id" column plus "Review Title" and/or "Review Content"',
    );
  }
  return rows
    .slice(1)
    .map((r) => {
      const title = titleIdx !== -1 ? (r[titleIdx] || "").trim() : "";
      const content = contentIdx !== -1 ? (r[contentIdx] || "").trim() : "";
      const rating = ratingIdx !== -1 ? parseInt(r[ratingIdx], 10) : NaN;
      return {
        id: (r[idIdx] || "").trim(),
        // First line = review title, rest = body — the convention the Vine
        // form autofill splits on.
        my_review: [title, content].filter(Boolean).join("\n\n"),
        my_rating: rating >= 1 && rating <= 5 ? rating : null,
      };
    })
    .filter((u) => u.id && (u.my_review || u.my_rating));
}
